<template>
  <div :class="isAnchor ? 'vs-anchor-base' : ''">
    <nav
      class="vs-ribbon-nav"
      :class="
        (isAnchor ? 'vs-anchor' : '') +
          ' ' +
          (isAnchor && isAnchorFixed ? 'vs-anchor-fixed' : '')
      "
      :style="anchorWidth >= 0 ? 'width:' + anchorWidth + 'px' : ''"
      :aria-label="label"
    >
      <ul
        class="vs-ribbon-nav-list"
        :style="
          'transform:translateX(' +
            (isRtl ? '' : '-') +
            scrollOffset +
            'px); transition: 0.25s linear'
        "
      >
        <template v-if="links">
          <li
            v-for="(link, idx) in JSON.parse(links)"
            :key="idx"
          >
            <RibbonItem
              :href="
                link.linktype === 'internal'
                  ? formatLink(link.internalLink)
                  : link.externalLink
              "
              :text="link.navigationLabel"
              :label="link.linkAriaLabel"
              :cta-target="link.ctaTarget"
              :linktype="link.linktype"
              @onKeydown="onkeydown_update"
              @onSwipeRight="onswipe_right"
              @onSwipeLeft="onswipe_left"
            />
          </li>
        </template>
        <slot />
      </ul>
      <button
        v-if="isRtl ? showNext : showPrev"
        class="vs-ribbon-btn vs-prev"
        tabindex="-1"
        @click="onclick_leftArrow()"
      >
        <span>Previous Slide</span>
      </button>
      <button
        v-if="isRtl ? showPrev : showNext"
        class="vs-ribbon-btn vs-next"
        tabindex="-1"
        @click="onclick_rightArrow()"
      >
        <span>Next Slide</span>
      </button>
    </nav>
  </div>
</template>

<script>
import RibbonItem from './ribbon-item/RibbonItem.vue';
import { animatedSmoothScroll } from '../../helpers/scrolling';

export default {
  name: 'VsRibbon',
  components: {
    RibbonItem,
  },
  props: {
    links: { type: String, default: '' },
    dir: { type: String, default: '' },
    type: { type: String, default: '' },
    label: { type: String, default: '' },
    initialindex: { type: String, default: '-1' },
  },
  data() {
    return {
      scrollOffset: 0,
      itemsWidth: 0,
      numItems: 0,
      totalOffsetWidth: 0,
      index: 0,
      showPrev: false,
      showNext: false,
      isRtl: false,
      isAnchor: false,
      isAnchorFixed: false,
      anchorHeight: -1,
      anchorWidth: -1,
      activeItem: 0,
    };
  },
  mounted() {
    this.init();
  },
  updated() {
    setTimeout(() => {
      this.init();
    }, 100);
  },
  methods: {
    init() {
      this.$nextTick(() => {
        this.isRtl = this.dir === 'rtl';
        this.isAnchor = this.type.toLowerCase() === 'anchor';
        this.oninit_setItemsWidth();
        this.oninit_showControls();
        this.oninit_setActiveItem();

        if (this.isAnchor) {
          this.oninit_initAnchor();
        }

        window.addEventListener('resize', () => {
          this.index = 0;
          this.scrollOffset = 0;
          this.setItemsWidth();
          this.showControls();
          this.setAnchorPosition();
          // this.updateAnchorScroll();
        });

        const links = this.$el.querySelectorAll('a');

        links.item(links.length - 1).addEventListener('focus', () => {
          if (this.showNext) {
            this.index = links.length - 1;
            this.scrollOffset = this.totalOffsetWidth;
            this.showControls();
          }
        });

        links.item(0).addEventListener('focus', () => {
          if (this.showPrev) {
            this.index = 0;
            this.scrollOffset = 0;
            this.showControls();
          }
        });

        for (let i = 0; i < links.length; i++) {
          const link = links[i];
          const href = link.getAttribute('href');

          if (href && href.startsWith('#')) {
            link.addEventListener('click', (e) => {
              e.preventDefault();
              const currentScroll = window.pageYOffset;
              const id = href.replace('#', '');
              document.location.hash = '';
              document.location.hash = id;
              animatedSmoothScroll(
                id,
                currentScroll,
                this.$el.getBoundingClientRect().height
              );
            });
          }
        }

        // if (this.isAnchor) {
        //   window.addEventListener('scroll', this.updateAnchorScroll, {passive: true});
        // }
      });
    },
    oninit_setItemsWidth() {
      this.setItemsWidth();
    },
    oninit_showControls() {
      this.showControls();
    },
    oninit_setActiveItem() {
      this.setActiveItem(parseInt(this.initialindex, 10));
    },
    oninit_initAnchor() {
      this.setAnchorPosition();
      this.updateAnchorScroll();
    },
    onswipe_right() {
      this.onclick_rightArrow();
    },
    onswipe_left() {
      this.onclick_leftArrow();
    },
    onclick_rightArrow() {
      this.isRtl ? this.prev() : this.next();
    },
    onclick_leftArrow() {
      this.isRtl ? this.next() : this.prev();
    },
    onkeydown_update(e) {
      if (e.code === 'Tab' && e.shiftKey) {
        this.prev();
      } else if (e.code === 'Tab') {
        this.next();
      }
    },
    setItemsWidth() {
      this.totalOffsetWidth = 0;
      this.itemsWidth = 0;

      const items = this.$el.querySelectorAll('li');

      this.numItems = items.length;

      items.forEach((el) => {
        if (!el.isEqualNode(items.item(items.length - 1))) {
          this.totalOffsetWidth += el.offsetWidth;
        }
        this.itemsWidth += el.offsetWidth;
      });
    },
    showControls() {
      this.showNext =
        this.itemsWidth > this.$el.getBoundingClientRect().width &&
        this.scrollOffset < this.totalOffsetWidth &&
        this.numItems > 1;
      this.showPrev = this.scrollOffset > 0 && this.numItems > 1;
    },
    setActiveItem(index) {
      const links = this.$el.getElementsByTagName('a');

      if (links && index !== -1) {
        links[this.activeItem].classList.remove('vs-active');
        this.activeItem = index;
        links[index].classList.add('vs-active');
        links[index].setAttribute('aria-current', 'Page');
        links[index].setAttribute('aria-disabled', true);
        links[index].setAttribute('tabindex', '-1');
      }
    },
    setAnchorPosition() {
      const anchorBase = this.$el;
      this.anchorHeight = anchorBase.offsetHeight;
      this.anchorWidth = anchorBase.offsetWidth;
    },
    prev() {
      if (this.index > 0 && this.showPrev) {
        this.index -= 1;
        const itemWidth =
          this.$el.getElementsByTagName('li')[this.index].offsetWidth;
        this.scrollOffset = Math.max(0, this.scrollOffset - itemWidth);
        this.showNext = true;
        this.showPrev = this.index !== 0;
      }
    },
    next() {
      if (this.index < this.numItems - 1 && this.showNext) {
        const itemWidth =
          this.$el.getElementsByTagName('li')[this.index].offsetWidth;
        this.scrollOffset = Math.min(
          this.scrollOffset + itemWidth,
          this.totalOffsetWidth
        );
        this.index += 1;
        this.showPrev = true;
        this.showNext = this.index !== this.numItems - 1;
      }
    },
    updateAnchorScroll() {
      const anchorBase = this.$el;
      this.isAnchorFixed = anchorBase.getBoundingClientRect().top < 0;
      const links = this.$el.getElementsByTagName('a');
      let highestScroll = undefined;
      let activeIndex = 0;

      for (let i = 0; i < links.length; i++) {
        const link = links[i];
        const href = link.getAttribute('href');

        if (href && href.startsWith('#')) {
          const id = href.replace('#', '');

          if (document.getElementById(id)) {
            const top =
              document.getElementById(id).getBoundingClientRect().top -
              this.$el.getBoundingClientRect().height -
              1;

            if (
              highestScroll === undefined ||
              (top > 0 && top < highestScroll) ||
              (top <= 0 && (highestScroll > 0 || top > highestScroll))
            ) {
              highestScroll = top;
              activeIndex = i;
            }
          }
        }
      }

      if (this.activeItem !== activeIndex) {
        this.setActiveItem(activeIndex);
      }
    },
    formatLink(link) {
      if (!link.startsWith('http') && !link.includes('.html')) {
        if (link.charAt(link.length - 1) === '/') {
          return link.substring(0, link.length - 1) + '.html';
        } else {
          return link + '.html';
        }
      } else {
        return link;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './ribbon';
@import '@/styles/core/types/types';
@import '@/styles/links/erclinks';
</style>
